import { useEffect } from "react";
import { SITE, BRAND_DOMAINS, PARTNERS, REGIONAL_PARTNERS } from "@/data/site";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SEO component: manages title, meta tags, canonical URL and JSON-LD structured data.
 */
export default function Seo({ title, description, path = "/", keywords = [], jsonLd }) {
  useEffect(() => {
    const url = `${SITE.domain}${path}`;
    document.title = title;

    upsertMeta("name", "description", description);
    if (keywords.length) upsertMeta("name", "keywords", keywords.join(", "));
    upsertMeta("name", "robots", "index, follow");

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:locale", "de_DE");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    upsertLink("canonical", url);

    // JSON-LD structured data
    const defaultLd = {
      "@context": "https://schema.org",
      "@type": "AutoRepair",
      name: SITE.name,
      description,
      url,
      telephone: SITE.phoneIntl,
      email: SITE.email,
      areaServed: "Cloppenburg, Oldenburger Münsterland",
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.address.street,
        postalCode: SITE.address.zip,
        addressLocality: SITE.address.city,
        addressCountry: "DE",
      },
      openingHours: "Mo-Su 00:00-23:59",
      priceRange: "€€",
      sameAs: [
        ...BRAND_DOMAINS,
        ...PARTNERS.map((p) => p.url),
        ...REGIONAL_PARTNERS.map((p) => p.url),
      ],
    };
    const ldData = jsonLd || defaultLd;
    let script = document.getElementById("seo-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "seo-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ldData);

    window.scrollTo(0, 0);
  }, [title, description, path, keywords, jsonLd]);

  return null;
}
